/**
 * Test Mode — a safe sandbox mounted at /admin/test. Admin-only (checks the stored
 * admin token). It renders Home / World / Progress with fully-unlocked MOCK state
 * and exposes buttons to trigger every celebration animation (Perfect Week,
 * level-up, achievement unlocked, room item unlocked, mystery reward unlocked).
 * It does NOT mutate real Marta's data — everything is local to this page.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Flame, Gift, Sparkles, Star, Trophy, Zap } from "lucide-react";
import Home from "@/pages/Home";
import World from "@/pages/World";
import Progress from "@/pages/Progress";
import CelebrationModal from "@/components/CelebrationModal";
import BadgeImage from "@/components/BadgeImage";
import { ALL_ACHIEVEMENTS, ALL_UNLOCKS } from "@/lib/testCatalog";

const LS_KEY = "marta.admin.token";

function buildMockState({ level = 3, xp = 300, streak = 2, longest = 4,
  perfect_weeks = 2, english_done = 7, drum_done = 6, allUnlocks = true, allAchievements = true } = {}) {
  const unlockedCodes = allUnlocks ? ALL_UNLOCKS.map(u => u.code) : [];
  const achEarned = allAchievements
    ? ALL_ACHIEVEMENTS.map(a => ({ code: a.code, earned_at: new Date().toISOString() }))
    : [];
  return {
    child: { name: "Marta", id: "marta" },
    xp,
    level: { level, current_xp: xp, level_min_xp: 225, level_max_xp: 375 },
    streak, longest_streak: longest,
    current_week: {
      id: "mock-week",
      start_date: new Date().toISOString().slice(0, 10),
      status: "normal",
      required_english_tasks: 2,
      required_drum_sessions: 3,
      minimum_drum_minutes: 20,
      perfect_week_completed_at: null,
      english_quests: [
        { id: "mock-eq-1", week_id: "mock-week", order: 0, title: "Workbook pages 22-23", instructions: "", content: "", attachment_url: null, external_url: null, tasks: [], completed_at: new Date().toISOString() },
        { id: "mock-eq-2", week_id: "mock-week", order: 1, title: "Vocabulary practice", instructions: "", content: "", attachment_url: null, external_url: null, tasks: [], completed_at: null },
      ],
      drum_sessions: [],
      english_done: 1,
      drum_done: 2,
      minutes_this_week: 42,
    },
    totals: { english_completed: english_done, drum_qualified: drum_done, perfect_weeks },
    month: { drum_sessions: drum_done, drum_minutes: drum_done * 22, english_quests: english_done, perfect_weeks },
    unlocks: { all: ALL_UNLOCKS, owned_codes: unlockedCodes, owned: unlockedCodes.map(c => ({ code: c, unlocked_at: new Date().toISOString() })) },
    achievements: { all: ALL_ACHIEVEMENTS, earned: achEarned },
    rewards: [
      { id: "mock-r1", internal_name: "New drum sticks", child_teaser: "A surprise just for you",
        requirement_type: "perfect_weeks", requirement_amount: 4, status: "locked", unlocked_at: null, created_at: new Date().toISOString() }
    ],
    activity: Array.from({ length: 8 }).map((_, i) => ({
      start_date: new Date(Date.now() - (7 - i) * 7 * 86400000).toISOString().slice(0, 10),
      english_done: i < 4 ? 2 : i === 4 ? 1 : 2,
      drum_done: i < 4 ? 3 : i === 4 ? 2 : 3,
      perfect: i < 4 || i > 4, status: "normal",
    })),
    config: { level_thresholds: [0, 50, 125, 225, 375, 550], xp_english: 20, xp_drum: 15, xp_perfect: 25 },
  };
}

export default function TestMode() {
  const nav = useNavigate();
  const authed = typeof window !== "undefined" && localStorage.getItem(LS_KEY);
  const [tab, setTab] = useState("home");

  // Mock state controls
  const [showAllUnlocks, setShowAllUnlocks] = useState(true);
  const [showAllAch, setShowAllAch] = useState(true);
  const [level, setLevel] = useState(3);

  const state = useMemo(() => buildMockState({
    level, xp: Math.min(300, 50 * level),
    allUnlocks: showAllUnlocks, allAchievements: showAllAch,
  }), [level, showAllUnlocks, showAllAch]);

  // Celebration triggers
  const [perfectWeek, setPerfectWeek] = useState(false);
  const [newUnlock, setNewUnlock] = useState(null);
  const [newReward, setNewReward] = useState(false);
  const [levelUp, setLevelUp] = useState(null);
  const [achievement, setAchievement] = useState(null);

  if (!authed) {
    return (
      <div className="min-h-dvh flex items-center justify-center text-center px-6">
        <div>
          <p className="micro-label">Locked</p>
          <h1 className="text-2xl text-[hsl(var(--ink))] mt-1">Test mode is parent-only</h1>
          <Link to="/admin" className="btn btn-primary mt-4">Open parent zone</Link>
        </div>
      </div>
    );
  }

  const tabs = [
    { k: "home", label: "Home" },
    { k: "world", label: "World" },
    { k: "progress", label: "Progress" },
  ];

  return (
    <div className="pb-28 pt-5">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => nav("/admin")} className="btn btn-ghost !py-2 !px-4 text-sm" data-testid="test-back-btn">
            <ArrowLeft className="w-4 h-4"/> Parent zone
          </button>
          <h1 className="text-[26px] text-[hsl(var(--ink))]">Test mode</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setPerfectWeek(true)}
            data-testid="trigger-perfect-week" className="btn btn-primary !py-2 !px-3 text-xs">
            <Star className="w-4 h-4"/> Perfect Week
          </button>
          <button onClick={() => setLevelUp(level + 1)}
            data-testid="trigger-level-up" className="btn btn-amber !py-2 !px-3 text-xs">
            <Zap className="w-4 h-4"/> Level Up
          </button>
          <button
            onClick={() => setNewUnlock(ALL_UNLOCKS[Math.floor(Math.random() * ALL_UNLOCKS.length)])}
            data-testid="trigger-unlock" className="btn btn-ghost !py-2 !px-3 text-xs">
            <Sparkles className="w-4 h-4"/> Unlock
          </button>
          <button
            onClick={() => {
              const a = ALL_ACHIEVEMENTS[Math.floor(Math.random() * ALL_ACHIEVEMENTS.length)];
              setAchievement({ code: a.code, name: a.name });
            }}
            data-testid="trigger-achievement" className="btn btn-ghost !py-2 !px-3 text-xs">
            <Trophy className="w-4 h-4"/> Achievement
          </button>
          <button onClick={() => setNewReward(true)}
            data-testid="trigger-reward" className="btn btn-ghost !py-2 !px-3 text-xs">
            <Gift className="w-4 h-4"/> Mystery
          </button>
        </div>
      </div>

      {/* State toggles */}
      <div className="mt-4 surface-soft p-4 flex items-center flex-wrap gap-4 text-sm">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={showAllUnlocks} onChange={(e) => setShowAllUnlocks(e.target.checked)}
            data-testid="toggle-unlocks" className="w-4 h-4 accent-[hsl(var(--violet))]"/>
          Fill all room unlocks
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={showAllAch} onChange={(e) => setShowAllAch(e.target.checked)}
            data-testid="toggle-achievements" className="w-4 h-4 accent-[hsl(var(--violet))]"/>
          Earn all achievements
        </label>
        <label className="flex items-center gap-2">
          <span className="micro-label">Level</span>
          <input type="range" min={0} max={5} value={level} onChange={(e) => setLevel(Number(e.target.value))}
            data-testid="level-slider" className="accent-[hsl(var(--violet))]"/>
          <span className="font-semibold">{level}</span>
        </label>
      </div>

      {/* Tabs */}
      <div className="mt-5 flex items-center gap-2">
        {tabs.map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`tab-${t.k}`}
            className={`px-4 py-2 rounded-full font-semibold text-sm transition
              ${tab === t.k
                ? "bg-[hsl(var(--violet))] text-white shadow-[0_8px_18px_-8px_hsla(258,45%,48%,0.6)]"
                : "bg-white text-[hsl(var(--ink))] shadow-[0_6px_14px_-10px_hsla(258,20%,20%,0.3)]"}`}
          >{t.label}</button>
        ))}
      </div>

      {/* Mock page preview */}
      <div className="mt-5 surface p-4 lg:p-6" data-testid={`preview-${tab}`}>
        {tab === "home" && <Home state={state} onOpenReward={() => setNewReward(true)}/>}
        {tab === "world" && <World state={state}/>}
        {tab === "progress" && <Progress state={state}/>}
      </div>

      {/* Modals */}
      <CelebrationModal
        open={perfectWeek} onClose={() => setPerfectWeek(false)}
        title="PERFECT WEEK!" subtitle="All 5 quests completed. +25 bonus XP"
        mascotState="celebrating" cta="Yay!" testid="perfect-week-modal"/>
      <CelebrationModal
        open={!!newUnlock} onClose={() => setNewUnlock(null)}
        title="New item unlocked!" subtitle={newUnlock?.name}
        mascotState="happy" cta="See my room" testid="unlock-modal"/>
      <CelebrationModal
        open={newReward} onClose={() => setNewReward(false)}
        title="MYSTERY REWARD UNLOCKED!" subtitle="Ask Mum/Dad about your surprise."
        emoji="🎁" cta="Awesome!" testid="reward-unlock-modal"/>
      <CelebrationModal
        open={!!levelUp} onClose={() => setLevelUp(null)}
        title={`Level ${levelUp}!`} subtitle="You just leveled up"
        mascotState="celebrating" cta="Keep going" testid="level-up-modal"/>
      <CelebrationModal
        open={!!achievement} onClose={() => setAchievement(null)}
        title="Achievement unlocked!" subtitle={achievement?.name}
        customVisual={achievement ? <BadgeImage code={achievement.code} size={128}/> : null}
        mascotState="achievement" cta="Nice!" testid="achievement-modal"/>
    </div>
  );
}
